import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Home, Trophy, Medal, Play, TrendingUp, Calendar, Clock, Share2, Gamepad2, Infinity, BarChart3, ArrowRight } from 'lucide-react';
import useGameStore from '../store/gameStore';
import BirdMascot from '../components/BirdMascot';
import BirdAvatar from '../components/BirdAvatar';
import ProfileModal from '../components/ProfileModal';
import ShareButton from '../components/ShareButton';
import AdSenseAd from '../components/AdSenseAd';
import NicknameSetup from '../components/NicknameSetup';
import MonthlyChampions from '../components/MonthlyChampions';
import MostReactedGallery from '../components/MostReactedGallery';
import { Sparkles } from 'lucide-react';

// Most Active Leaderboard Component
const MostActiveLeaderboard = () => {
  const [dailyLeaderboard, setDailyLeaderboard] = React.useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = React.useState([]);
  const [monthlyLeaderboard, setMonthlyLeaderboard] = React.useState([]);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchMostActive = async () => {
      try {
        const [dailyRes, weeklyRes, monthlyRes, allTimeRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/most-active/daily?limit=10`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/most-active/weekly?limit=10`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/most-active/monthly?limit=10`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/most-active/all-time?limit=10`)
        ]);
        
        if (dailyRes.ok) {
          const data = await dailyRes.json();
          if (data.success) setDailyLeaderboard(data.leaderboard);
        }
        if (weeklyRes.ok) {
          const data = await weeklyRes.json();
          if (data.success) setWeeklyLeaderboard(data.leaderboard);
        }
        if (monthlyRes.ok) {
          const data = await monthlyRes.json();
          if (data.success) setMonthlyLeaderboard(data.leaderboard);
        }
        if (allTimeRes.ok) {
          const data = await allTimeRes.json();
          if (data.success) setAllTimeLeaderboard(data.leaderboard);
        }
      } catch (error) {
        console.error('Error fetching most active leaderboard:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMostActive();
  }, []);

  const getRankBadge = (rank) => {
    if (rank === 1) return <span className="text-lg">🥇</span>;
    if (rank === 2) return <span className="text-lg">🥈</span>;
    if (rank === 3) return <span className="text-lg">🥉</span>;
    return <span className="text-sm font-bold text-muted-foreground">#{rank}</span>;
  };

  const renderLeaderboard = (leaderboard, emptyMessage) => {
    if (isLoading) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto mb-2" />
          Loading...
        </div>
      );
    }
    
    if (leaderboard.length === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          {emptyMessage}
        </div>
      );
    }
    
    return leaderboard.map((player, index) => (
      <motion.div
        key={index}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.05 }}
        whileHover={{ x: 5 }}
        className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all ${
          player.rank <= 3
            ? 'bg-muted/50 border-primary/30'
            : 'bg-card border-border hover:border-primary/20'
        }`}
      >
        <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0 ${
          player.rank <= 3 ? 'bg-primary/20' : 'bg-muted'
        }`}>
          {getRankBadge(player.rank)}
        </div>

        <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0">
          <BirdAvatar avatarId={player.avatar_id || 1} size={32} animate={false} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
            <span className="font-semibold truncate text-sm sm:text-base">{player.nickname}</span>
          </div>
          <div className="text-xs sm:text-sm text-muted-foreground truncate">
            {player.score.toLocaleString()} pts • {player.accuracy}% accuracy
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-3 shrink-0">
          <div className="text-right">
            <div className="text-base sm:text-xl font-heading font-bold text-gradient-primary whitespace-nowrap">
              {player.games_played.toLocaleString()}
            </div>
            <div className="text-xs text-muted-foreground hidden sm:block">games</div>
          </div>
        </div>
      </motion.div>
    ));
  };

  return (
    <Card className="p-4 md:p-6 bg-gradient-card border-border mt-4 md:mt-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-heading font-bold flex items-center gap-2">
          <Gamepad2 className="w-5 h-5 md:w-6 md:h-6 text-primary" />
          Most Games Played
        </h2>
        <p className="text-xs text-muted-foreground mt-1">Showing top 10 players</p>
      </div>

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-4 md:mb-6">
          <TabsTrigger value="daily" className="text-xs md:text-sm px-1 md:px-3">
            <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1" />
            <span className="hidden sm:inline">Daily</span>
            <span className="sm:hidden">Day</span>
          </TabsTrigger>
          <TabsTrigger value="weekly" className="text-xs md:text-sm px-1 md:px-3">
            <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1" />
            <span className="hidden sm:inline">Weekly</span>
            <span className="sm:hidden">Week</span>
          </TabsTrigger>
          <TabsTrigger value="monthly" className="text-xs md:text-sm px-1 md:px-3">
            <Trophy className="w-3 h-3 md:w-4 md:h-4 mr-1" />
            <span className="hidden sm:inline">Monthly</span>
            <span className="sm:hidden">Month</span>
          </TabsTrigger>
          <TabsTrigger value="all-time" className="text-xs md:text-sm px-1 md:px-3" data-testid="most-active-all-time-tab">
            <Infinity className="w-3 h-3 md:w-4 md:h-4 mr-1" />
            <span className="hidden sm:inline">All Time</span>
            <span className="sm:hidden">All</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-3">
          {renderLeaderboard(dailyLeaderboard, "No games played today. Be the first!")}
        </TabsContent>

        <TabsContent value="weekly" className="space-y-3">
          {renderLeaderboard(weeklyLeaderboard, "No games played this week. Be the first!")}
        </TabsContent>

        <TabsContent value="monthly" className="space-y-3">
          {renderLeaderboard(monthlyLeaderboard, "No games played this month. Be the first!")}
        </TabsContent>

        <TabsContent value="all-time" className="space-y-3" data-testid="most-active-all-time-content">
          {renderLeaderboard(allTimeLeaderboard, "No players yet. Be the first!")}
        </TabsContent>
      </Tabs>
    </Card>
  );
};

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { nickname, avatarId } = useGameStore();
  const [showNicknameSetup, setShowNicknameSetup] = React.useState(false);
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const [dailyLeaderboard, setDailyLeaderboard] = React.useState([]);
  const [weeklyLeaderboard, setWeeklyLeaderboard] = React.useState([]);
  const [monthlyLeaderboard, setMonthlyLeaderboard] = React.useState([]);
  const [allTimeLeaderboard, setAllTimeLeaderboard] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [defaultTab, setDefaultTab] = React.useState('monthly');
  const [globalStats, setGlobalStats] = React.useState({
    total_games: 0,
    unique_players: 0,
    highest_score: 0
  });
  const [playerStats, setPlayerStats] = React.useState({
    totalScore: 0,
    gamesPlayed: 0,
    jailBirdBonusesWon: 0
  });
  const [playerRankings, setPlayerRankings] = React.useState(null);
  
  // Fetch leaderboards on mount, when nickname changes, or when navigating to this page
  React.useEffect(() => {
    const fetchLeaderboards = async () => {
      setIsLoading(true);
      try {
        // Fetch all data in parallel
        const fetchPromises = [
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/daily`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/weekly`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/monthly`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/all-time`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/stats`)
        ];
        
        // Add player stats and rankings fetch if nickname exists
        if (nickname) {
          fetchPromises.push(fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/player/${nickname}/stats`));
          fetchPromises.push(fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/player/${nickname}/rankings`));
        }
        
        const results = await Promise.all(fetchPromises);
        const [dailyRes, weeklyRes, monthlyRes, allTimeRes, statsRes, playerRes, rankingsRes] = results;
        
        let dailyData, weeklyData, monthlyData, allTimeData;
        
        if (dailyRes.ok) {
          dailyData = await dailyRes.json();
          setDailyLeaderboard(dailyData.leaderboard || []);
        }
        
        if (weeklyRes.ok) {
          weeklyData = await weeklyRes.json();
          setWeeklyLeaderboard(weeklyData.leaderboard || []);
        }
        
        if (monthlyRes.ok) {
          monthlyData = await monthlyRes.json();
          setMonthlyLeaderboard(monthlyData.leaderboard || []);
        }
        
        if (allTimeRes.ok) {
          allTimeData = await allTimeRes.json();
          setAllTimeLeaderboard(allTimeData.leaderboard || []);
        }
        
        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setGlobalStats(statsData);
        }
        
        if (playerRes && playerRes.ok) {
          const playerData = await playerRes.json();
          if (playerData.success) {
            setPlayerStats({
              totalScore: playerData.player.total_score || 0,
              gamesPlayed: playerData.player.games_played || 0,
              jailBirdBonusesWon: playerData.player.jailbird_bonuses_won || 0
            });
          }
        }
        
        // Fetch player rankings
        if (rankingsRes && rankingsRes.ok) {
          const rankingsData = await rankingsRes.json();
          if (rankingsData.success) {
            setPlayerRankings(rankingsData.rankings);
          }
        }
        
        // Always default to daily tab for all players
        setDefaultTab('daily');
      } catch (error) {
        console.error('Error fetching leaderboards:', error);
        // Keep empty arrays on error
        setDailyLeaderboard([]);
        setWeeklyLeaderboard([]);
        setMonthlyLeaderboard([]);
        setAllTimeLeaderboard([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchLeaderboards();
  }, [nickname, location.key]); // Refetch when route changes
  
  // Refetch data when page becomes visible (user returns from game)
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page is now visible, refetch data
        const fetchPromises = [
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/daily?limit=10`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/weekly?limit=10`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/monthly?limit=10`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/all-time?limit=10`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/stats`)
        ];
        
        if (nickname) {
          fetchPromises.push(fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/player/${nickname}/stats`));
        }
        
        Promise.all(fetchPromises).then(async (results) => {
          const [dailyRes, weeklyRes, monthlyRes, allTimeRes, statsRes, playerRes] = results;
          
          if (dailyRes.ok) {
            const data = await dailyRes.json();
            setDailyLeaderboard(data.leaderboard || []);
          }
          
          if (weeklyRes.ok) {
            const data = await weeklyRes.json();
            setWeeklyLeaderboard(data.leaderboard || []);
          }
          
          if (monthlyRes.ok) {
            const data = await monthlyRes.json();
            setMonthlyLeaderboard(data.leaderboard || []);
          }
          
          if (allTimeRes.ok) {
            const data = await allTimeRes.json();
            setAllTimeLeaderboard(data.leaderboard || []);
          }
          
          if (statsRes.ok) {
            const data = await statsRes.json();
            setGlobalStats(data);
          }
          
          if (playerRes && playerRes.ok) {
            const data = await playerRes.json();
            if (data.success) {
              setPlayerStats({
                totalScore: data.player.total_score || 0,
                gamesPlayed: data.player.games_played || 0,
                jailBirdBonusesWon: data.player.jailbird_bonuses_won || 0
              });
            }
          }
        });
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [nickname]);
  
  // Listen for storage changes and game completion events
  React.useEffect(() => {
    const handleStorageChange = () => {
      // Refetch all data when localStorage changes
      if (nickname) {
        Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/daily`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/weekly`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/monthly`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/leaderboard/all-time`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/stats`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/scores/player/${nickname}/stats`)
        ]).then(async ([dailyRes, weeklyRes, monthlyRes, allTimeRes, statsRes, playerRes]) => {
          if (dailyRes.ok) {
            const data = await dailyRes.json();
            setDailyLeaderboard(data.leaderboard || []);
          }
          if (weeklyRes.ok) {
            const data = await weeklyRes.json();
            setWeeklyLeaderboard(data.leaderboard || []);
          }
          if (monthlyRes.ok) {
            const data = await monthlyRes.json();
            setMonthlyLeaderboard(data.leaderboard || []);
          }
          if (allTimeRes.ok) {
            const data = await allTimeRes.json();
            setAllTimeLeaderboard(data.leaderboard || []);
          }
          if (statsRes.ok) {
            const data = await statsRes.json();
            setGlobalStats(data);
          }
          if (playerRes.ok) {
            const data = await playerRes.json();
            if (data.success) {
              setPlayerStats({
                totalScore: data.player.total_score || 0,
                gamesPlayed: data.player.games_played || 0,
                jailBirdBonusesWon: data.player.jailbird_bonuses_won || 0
              });
            }
          }
        });
      }
    };
    
    const handleGameCompleted = () => {
      // Force refetch when game completes
      handleStorageChange();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('gameCompleted', handleGameCompleted);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('gameCompleted', handleGameCompleted);
    };
  }, [nickname]);

  const getRankColor = (rank) => {
    if (rank === 1) return 'text-gold';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-amber-600';
    return 'text-muted-foreground';
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <Trophy className="w-5 h-5 text-gold" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-lg font-bold text-muted-foreground">#{rank}</span>;
  };

  return (
    <div className="min-h-screen bg-background p-2 sm:p-4">
      {/* Profile Modal */}
      <ProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {showNicknameSetup && (
        <NicknameSetup
          onComplete={() => {
            setShowNicknameSetup(false);
            navigate('/game');
          }}
          onClose={() => setShowNicknameSetup(false)}
        />
      )}
      
      {/* Header */}
      <div className="container mx-auto max-w-6xl mb-4 sm:mb-8">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-muted-foreground hover:text-foreground"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
            
            {nickname && (
              <button
                onClick={() => setShowProfileModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 hover:bg-primary/20 hover:border-primary/30 transition-all cursor-pointer"
              >
                <BirdAvatar avatarId={avatarId} size={24} />
                <span className="text-sm font-semibold text-primary">{nickname}</span>
              </button>
            )}
          </div>
          
          <Button
            onClick={() => {
              const storedNickname = localStorage.getItem('jailbirds_nickname');
              if (storedNickname) {
                // Returning player - go directly to game
                navigate('/game');
              } else {
                // New player - show nickname setup
                setShowNicknameSetup(true);
              }
            }}
            className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold"
          >
            <Play className="w-4 h-4 mr-2" />
            Play
          </Button>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="flex justify-center gap-3 mb-4">
            <BirdMascot size={60} color="#10B981" />
            <BirdMascot size={60} color="#FF6B6B" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold mb-2">
            Global <span className="text-gradient-primary">Leaderboard</span>
          </h1>
          <p className="text-base sm:text-xl text-muted-foreground mb-4">
            Compete with the best detectives worldwide
          </p>
          
          {/* Prize Notice */}
          <div className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 max-w-md mx-auto mb-4">
            <div className="text-center">
              <div className="text-sm font-semibold text-amber-500 dark:text-amber-400 mb-1">
                Monthly Contest
              </div>
              <div className="text-lg font-bold text-amber-500 dark:text-amber-400 mb-1">
                Win a JailBirdsGame.com T-Shirt!
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground space-y-0.5">
                <div>Most games played</div>
                <div>Top monthly points</div>
              </div>
            </div>
          </div>
          
          {/* Social Share Buttons - Hidden on mobile */}
          <div className="hidden md:flex flex-col items-center gap-2 mt-4">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Challenge your friends!
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const shareText = `Check out the JailBirds Leaderboard! Can you guess the crime from the mugshot? Challenge yourself and compete for the top spot!`;
                  const shareUrl = window.location.origin;
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank', 'width=600,height=400');
                }}
                className="bg-[#1877F2] hover:bg-[#166FE5] text-white border-0 gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const shareText = `Check out the JailBirds Leaderboard! Can you guess the crime from the mugshot? Challenge yourself! ${window.location.origin}`;
                  window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank', 'width=600,height=400');
                }}
                className="bg-black hover:bg-gray-800 text-white border-0 gap-2"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X (Twitter)
              </Button>
            </div>
          </div>
        </motion.div>
      </div>

      {/* 10 Most Reacted Mugshots Gallery */}
      <MostReactedGallery />

      <div className="container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-3 gap-4 md:gap-6">
          {/* Main Leaderboard */}
          <div className="lg:col-span-2 order-1">
            <Card className="p-4 md:p-6 bg-gradient-card border-border">
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                  Top Players
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Showing top 10 players</p>
              </div>

              <Tabs defaultValue={defaultTab} key={defaultTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-4 md:mb-6">
                  <TabsTrigger value="daily" className="text-xs md:text-sm px-1 md:px-3">
                    <Clock className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                    <span className="hidden sm:inline">Daily</span>
                    <span className="sm:hidden">Day</span>
                  </TabsTrigger>
                  <TabsTrigger value="weekly" className="text-xs md:text-sm px-1 md:px-3">
                    <Calendar className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                    <span className="hidden sm:inline">Weekly</span>
                    <span className="sm:hidden">Week</span>
                  </TabsTrigger>
                  <TabsTrigger value="monthly" className="text-xs md:text-sm px-1 md:px-3">
                    <Trophy className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                    <span className="hidden sm:inline">Monthly</span>
                    <span className="sm:hidden">Month</span>
                  </TabsTrigger>
                  <TabsTrigger value="all-time" className="text-xs md:text-sm px-1 md:px-3" data-testid="all-time-tab">
                    <Infinity className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                    <span className="hidden sm:inline">All Time</span>
                    <span className="sm:hidden">All</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="daily" className="space-y-3">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto mb-2" />
                      Loading leaderboard...
                    </div>
                  ) : dailyLeaderboard.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No games played in the last 24 hours. Be the first!
                    </div>
                  ) : (
                    dailyLeaderboard.map((player, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ x: 5 }}
                    className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all ${
                      player.rank <= 3
                        ? 'bg-muted/50 border-primary/30'
                        : 'bg-card border-border hover:border-primary/20'
                    }`}
                  >
                    <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0 ${
                      player.rank <= 3 ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      {getRankBadge(player.rank)}
                    </div>

                    <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                      <BirdAvatar avatarId={player.avatar_id || 1} size={32} animate={false} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                        <span className="font-semibold truncate text-sm sm:text-base">{player.nickname}</span>
                      </div>
                      <div className="text-xs sm:text-sm text-muted-foreground truncate">
                        {player.games_played} {player.games_played === 1 ? 'game' : 'games'} • {player.accuracy}%
                      </div>
                    </div>

                    <div className="flex items-center gap-1 sm:gap-3 shrink-0">
                      <div className="text-right">
                        <div className="text-base sm:text-xl font-heading font-bold text-gradient-primary whitespace-nowrap">
                          {player.score.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground hidden sm:block">points</div>
                      </div>
                      <ShareButton
                        nickname={player.nickname}
                        avatarId={player.avatar_id || 1}
                        totalScore={player.score}
                        gamesPlayed={player.games_played}
                        accuracy={player.accuracy}
                        variant="ghost"
                        size="sm"
                        showLabel={false}
                        className="shrink-0 hidden sm:flex"
                      />
                    </div>
                  </motion.div>
                    ))
                  )}
                </TabsContent>

                <TabsContent value="weekly" className="space-y-3">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto mb-2" />
                      Loading leaderboard...
                    </div>
                  ) : weeklyLeaderboard.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No games played in the last week. Be the first!
                    </div>
                  ) : (
                    weeklyLeaderboard.map((player, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ x: 5 }}
                      className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all ${
                        player.rank <= 3
                          ? 'bg-muted/50 border-primary/30'
                          : 'bg-card border-border hover:border-primary/20'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0 ${
                        player.rank <= 3 ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        {getRankBadge(player.rank)}
                      </div>

                      <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                        <BirdAvatar avatarId={player.avatar_id || 1} size={32} animate={false} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                          <span className="font-semibold truncate text-sm sm:text-base">{player.nickname}</span>
                        </div>
                        <div className="text-xs sm:text-sm text-muted-foreground truncate">
                          {player.games_played} {player.games_played === 1 ? 'game' : 'games'} • {player.accuracy}%
                        </div>
                      </div>

                      <div className="flex items-center gap-1 sm:gap-3 shrink-0">
                        <div className="text-right">
                          <div className="text-base sm:text-xl font-heading font-bold text-gradient-primary whitespace-nowrap">
                            {player.score.toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground hidden sm:block">points</div>
                        </div>
                        <ShareButton
                          nickname={player.nickname}
                          avatarId={player.avatar_id || 1}
                          totalScore={player.score}
                          gamesPlayed={player.games_played}
                          accuracy={player.accuracy}
                          variant="ghost"
                          size="sm"
                          showLabel={false}
                          className="shrink-0 hidden sm:flex"
                        />
                      </div>
                    </motion.div>
                    ))
                  )}
                </TabsContent>

                <TabsContent value="monthly" className="space-y-3">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto mb-2" />
                      Loading leaderboard...
                    </div>
                  ) : monthlyLeaderboard.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No players yet. Be the first to play!
                    </div>
                  ) : (
                    monthlyLeaderboard.map((player, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ x: 5 }}
                      className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all ${
                        player.rank <= 3
                          ? 'bg-muted/50 border-primary/30'
                          : 'bg-card border-border hover:border-primary/20'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0 ${
                        player.rank <= 3 ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        {getRankBadge(player.rank)}
                      </div>

                      <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                        <BirdAvatar avatarId={player.avatar_id || 1} size={32} animate={false} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                          <span className="font-semibold truncate text-sm sm:text-base">{player.nickname}</span>
                        </div>
                        <div className="text-xs sm:text-sm text-muted-foreground truncate">
                          {player.games_played} {player.games_played === 1 ? 'game' : 'games'} • {player.accuracy}%
                        </div>
                      </div>

                      <div className="flex items-center gap-1 sm:gap-3 shrink-0">
                        <div className="text-right">
                          <div className="text-base sm:text-xl font-heading font-bold text-gradient-primary whitespace-nowrap">
                            {player.score.toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground hidden sm:block">points</div>
                        </div>
                        <ShareButton
                          nickname={player.nickname}
                          avatarId={player.avatar_id || 1}
                          totalScore={player.score}
                          gamesPlayed={player.games_played}
                          accuracy={player.accuracy}
                          variant="ghost"
                          size="sm"
                          showLabel={false}
                          className="shrink-0 hidden sm:flex"
                        />
                      </div>
                    </motion.div>
                    ))
                  )}
                </TabsContent>

                <TabsContent value="all-time" className="space-y-3" data-testid="all-time-content">
                  {isLoading ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent mx-auto mb-2" />
                      Loading leaderboard...
                    </div>
                  ) : allTimeLeaderboard.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No players yet. Be the first to play!
                    </div>
                  ) : (
                    allTimeLeaderboard.map((player, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      whileHover={{ x: 5 }}
                      className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-all ${
                        player.rank <= 3
                          ? 'bg-muted/50 border-primary/30'
                          : 'bg-card border-border hover:border-primary/20'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full shrink-0 ${
                        player.rank <= 3 ? 'bg-primary/20' : 'bg-muted'
                      }`}>
                        {getRankBadge(player.rank)}
                      </div>

                      <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                        <BirdAvatar avatarId={player.avatar_id || 1} size={32} animate={false} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                          <span className="font-semibold truncate text-sm sm:text-base">{player.nickname}</span>
                        </div>
                        <div className="text-xs sm:text-sm text-muted-foreground truncate">
                          {player.games_played} {player.games_played === 1 ? 'game' : 'games'} • {player.accuracy}%
                        </div>
                      </div>

                      <div className="flex items-center gap-1 sm:gap-3 shrink-0">
                        <div className="text-right">
                          <div className="text-base sm:text-xl font-heading font-bold text-gradient-primary whitespace-nowrap">
                            {player.score.toLocaleString()}
                          </div>
                          <div className="text-xs text-muted-foreground hidden sm:block">points</div>
                        </div>
                        <ShareButton
                          nickname={player.nickname}
                          avatarId={player.avatar_id || 1}
                          totalScore={player.score}
                          gamesPlayed={player.games_played}
                          accuracy={player.accuracy}
                          variant="ghost"
                          size="sm"
                          showLabel={false}
                          className="shrink-0 hidden sm:flex"
                        />
                      </div>
                    </motion.div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </Card>
            
            {/* Most Games Played Section */}
            <MostActiveLeaderboard />
            
            {/* Hall of Champions Section */}
            <MonthlyChampions className="mt-4 md:mt-6" />
          </div>

          {/* Player Stats Sidebar */}
          <div className="space-y-4 md:space-y-6 order-2 lg:order-none">
            {/* Your Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 bg-gradient-card border-border">
                <h3 className="text-xl font-heading font-semibold mb-4 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-primary" />
                  Your Stats
                </h3>

                {nickname ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-12 h-12">
                        <BirdAvatar avatarId={avatarId} size={48} />
                      </div>
                      <div>
                        <div className="font-semibold">{nickname}</div>
                        <div className="text-sm text-muted-foreground">Player</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 rounded-lg bg-muted/50">
                        <span className="text-sm text-muted-foreground">Total Score</span>
                        <span className="text-lg font-heading font-bold text-gradient-primary">
                          {playerStats.totalScore.toLocaleString()}
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 rounded-lg bg-muted/50">
                        <span className="text-sm text-muted-foreground">Games Played</span>
                        <span className="text-lg font-heading font-bold">
                          {playerStats.gamesPlayed}
                        </span>
                      </div>

                      <div className="flex justify-between items-center p-3 rounded-lg bg-gold/10 border border-gold/20">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-gold" />
                          <span className="text-sm text-gold font-semibold">JailBird Bonuses</span>
                        </div>
                        <span className="text-lg font-heading font-bold text-gold">
                          {playerStats.jailBirdBonusesWon}
                        </span>
                      </div>

                      {playerRankings && (
                        <>
                          <div className="border-t border-border my-3"></div>
                          <div className="space-y-2">
                            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                              Global Rankings
                            </div>
                            <div className="flex justify-between items-center p-3 rounded-lg bg-primary/5 border border-primary/20">
                              <span className="text-sm text-muted-foreground">Points Rank</span>
                              <span className="text-lg font-heading font-bold text-primary">
                                #{playerRankings.points_rank} <span className="text-xs text-muted-foreground">/ {playerRankings.total_players}</span>
                              </span>
                            </div>
                            <div className="flex justify-between items-center p-3 rounded-lg bg-primary/5 border border-primary/20">
                              <span className="text-sm text-muted-foreground">Games Rank</span>
                              <span className="text-lg font-heading font-bold text-primary">
                                #{playerRankings.games_rank} <span className="text-xs text-muted-foreground">/ {playerRankings.total_players}</span>
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted-foreground mb-4">
                      Play a game to see your stats!
                    </p>
                    <Button
                      onClick={() => {
                        const storedNickname = localStorage.getItem('jailbirds_nickname');
                        if (storedNickname) {
                          // Returning player - go directly to game
                          navigate('/game');
                        } else {
                          // New player - show nickname setup
                          setShowNicknameSetup(true);
                        }
                      }}
                      className="bg-gradient-primary text-primary-foreground"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Start Playing
                    </Button>
                  </div>
                )}
              </Card>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="p-6 bg-gradient-card border-border">
                <h3 className="text-xl font-heading font-semibold mb-4">Quick Stats</h3>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Players</span>
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                      {globalStats.unique_players || 0}
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Games</span>
                    <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20">
                      {globalStats.total_games || 0}
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Highest Score</span>
                    <Badge variant="outline" className="bg-gold/10 text-gold border-gold/20">
                      {globalStats.highest_score?.toLocaleString() || 0}
                    </Badge>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* AdSense Sidebar Ad */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <AdSenseAd 
                adSlot="1234567890"
                adFormat="auto"
                placement="leaderboard"
                className="rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Community Stats CTA */}
      <div className="container mx-auto max-w-6xl mt-8 md:mt-12 px-2 sm:px-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <Card className="p-6 sm:p-8 bg-gradient-card border-primary/30 relative overflow-hidden">
            <div className="absolute -right-8 -top-8 opacity-10">
              <BarChart3 className="w-40 h-40 text-primary" />
            </div>
            <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
              <div>
                <h3 className="text-xl sm:text-2xl font-heading font-bold mb-1">
                  Explore the <span className="text-gradient-primary">Community Stats</span>
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground max-w-xl">
                  Dive into all-time data, cool graphs, and category leaders — then see exactly where you stand against the whole flock.
                </p>
              </div>
              <Button
                size="lg"
                onClick={() => navigate('/community-stats')}
                className="bg-gradient-primary hover-glow-primary text-primary-foreground font-semibold shrink-0"
                data-testid="view-community-stats-btn"
              >
                <BarChart3 className="w-5 h-5 mr-2" />
                View Stats
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
      
      {/* Footer */}
      <footer className="border-t border-border bg-card/50 backdrop-blur-sm py-8 mt-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a href="/about" className="text-primary hover:underline font-medium">
                About
              </a>
              <span className="text-muted-foreground">•</span>
              <a href="/legal" className="text-primary hover:underline font-medium">
                Privacy & Legal
              </a>
              <span className="text-muted-foreground">•</span>
              <a href="mailto:support@jailbirdsgame.com" className="text-primary hover:underline font-medium">
                Contact
              </a>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              © 2025 JailBirds. Owned and operated by M.Panepinto. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LeaderboardPage;
